import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import properties from "../constantes/constImoveis";
import PropertyCard from "./PropertyCard";
import "../css/properties.css";

const normalizeText = (value) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const cityFromQuery = (city) => {
  const normalizedCity = normalizeText(city || "");
  if (normalizedCity.includes("sapucaia")) return "sapucaia do sul";
  if (normalizedCity.includes("esteio")) return "esteio";
  if (normalizedCity.includes("sao leopoldo")) return "sao leopoldo";
  return "";
};

const typeFromQuery = (type) => {
  if (type === "1" || type === "apartamento") return "apartamento";
  if (type === "2" || type === "casa") return "casa";
  return "";
};

const formatPriceInput = (value) => value.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

const parsePriceInput = (value) => value.replace(/\D/g, "");

const Properties = ({
  cardsPerRow = 4,
  onCardsPerRowChange = () => {},
  configError = "",
  isPreview = false,
}) => {
  const [routeSearchParams, setRouteSearchParams] = useSearchParams();
  const [previewSearchParams, setPreviewSearchParams] = useState(
    () => new URLSearchParams(),
  );
  const searchParams = isPreview ? previewSearchParams : routeSearchParams;
  const setSearchParams = isPreview
    ? setPreviewSearchParams
    : setRouteSearchParams;
  const search = searchParams.get("search") || "";
  const city = cityFromQuery(searchParams.get("city"));
  const type = typeFromQuery(searchParams.get("type"));
  const bedrooms = searchParams.get("bedrooms") || "";
  const minPrice = searchParams.get("minPrice") || "";
  const maxPrice = searchParams.get("maxPrice") || "";
  const sortOrder = searchParams.get("sort") || "recent";
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);

  const updateSearchParam = (key, value) => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        if (value) nextParams.set(key, value);
        else nextParams.delete(key);
        return nextParams;
      },
      { replace: true },
    );
  };

  const filteredProperties = properties
    .filter((property) => {
      const normalizedCity = cityFromQuery(property.city);
      const normalizedType = normalizeText(property.type);
      const matchesType =
        !type ||
        (type === "apartamento"
          ? normalizedType.includes("apartamento") ||
            normalizedType.includes("cobertura")
          : normalizedType === "casa" || normalizedType === "sobrado");
      const searchTerms = normalizeText(search);
      const matchesSearch =
        !searchTerms ||
        normalizeText(
          `${property.code} ${property.type} ${property.city} ${property.neighborhood} ${property.condominium}`,
        ).includes(searchTerms);
      const minPriceValue = Number(minPrice);
      const maxPriceValue = Number(maxPrice);
      const matchesPrice =
        (!minPrice ||
          (Number.isFinite(minPriceValue) &&
            property.price >= minPriceValue)) &&
        (!maxPrice ||
          (Number.isFinite(maxPriceValue) && property.price <= maxPriceValue));

      return (
        (!city || normalizedCity === city) &&
        matchesType &&
        (!bedrooms || property.bedrooms >= Number(bedrooms)) &&
        matchesSearch &&
        matchesPrice
      );
    })
    .sort((first, second) => {
      if (sortOrder === "price-asc") return first.price - second.price;
      if (sortOrder === "price-desc") return second.price - first.price;
      return 0;
    });

  const clearFilters = () => {
    setSearchParams(isPreview ? new URLSearchParams() : {});
  };

  return (
    <main className="properties-page">
      <div className="properties-container">
        <div className="properties-toolbar">
          <p className="properties-count" aria-live="polite">
            {filteredProperties.length}{" "}
            {filteredProperties.length === 1
              ? "imóvel encontrado"
              : "imóveis encontrados"}
          </p>
          {configError && (
            <p className="properties-config-error" role="alert">
              {configError}
            </p>
          )}

          <div className="properties-controls">
            <label className="properties-sort">
              <span>Ordenar por</span>
              <span className="properties-sort-select-wrap">
                <select
                  value={sortOrder}
                  onChange={(event) =>
                    updateSearchParam(
                      "sort",
                      event.target.value === "recent" ? "" : event.target.value,
                    )
                  }
                >
                  <option value="recent">Mais recentes</option>
                  <option value="price-asc">Menor preço</option>
                  <option value="price-desc">Maior preço</option>
                </select>
                <span className="properties-sort-chevron" aria-hidden="true" />
              </span>
            </label>
            <label className="properties-columns-control">
              <span>Cartões por linha</span>
              <span className="properties-sort-select-wrap">
                <select
                  value={cardsPerRow}
                  aria-label="Cartões por linha"
                  onChange={(event) =>
                    onCardsPerRowChange(Number(event.target.value))
                  }
                >
                  {[1, 2, 3, 4].map((columns) => (
                    <option value={columns} key={columns}>
                      {columns}
                    </option>
                  ))}
                </select>
                <span className="properties-sort-chevron" aria-hidden="true" />
              </span>
            </label>
            <button
              className="properties-control-button"
              type="button"
              aria-expanded={areFiltersOpen}
              aria-controls="properties-filters"
              onClick={() => setAreFiltersOpen((open) => !open)}
            >
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <path d="M3 5h14M6 10h8m-5 5h2" />
              </svg>
              Mais filtros
            </button>
            <button
              className="properties-control-button"
              type="button"
              onClick={clearFilters}
            >
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <path d="m5 5 10 10M15 5 5 15" />
              </svg>
              Limpar filtros
            </button>
          </div>
        </div>

        {areFiltersOpen && (
          <section
            className="properties-filter-panel"
            id="properties-filters"
            aria-label="Filtros de imóveis"
          >
            <label className="properties-filter-field">
              <span>Buscar por código ou localização</span>
              <input
                type="search"
                value={search}
                onChange={(event) =>
                  updateSearchParam("search", event.target.value)
                }
                placeholder="Código, bairro ou condomínio"
              />
            </label>
            <label className="properties-filter-field">
              <span>Cidade</span>
              <select
                value={city}
                onChange={(event) =>
                  updateSearchParam("city", event.target.value)
                }
              >
                <option value="">Todas as cidades</option>
                <option value="esteio">Esteio</option>
                <option value="sapucaia do sul">Sapucaia do Sul</option>
                <option value="sao leopoldo">São Leopoldo</option>
              </select>
            </label>
            <label className="properties-filter-field">
              <span>Tipo de imóvel</span>
              <select
                value={type}
                onChange={(event) =>
                  updateSearchParam("type", event.target.value)
                }
              >
                <option value="">Todos os tipos</option>
                <option value="casa">Casa</option>
                <option value="apartamento">Apartamento</option>
              </select>
            </label>
            <label className="properties-filter-field">
              <span>Quartos</span>
              <select
                value={bedrooms}
                onChange={(event) =>
                  updateSearchParam("bedrooms", event.target.value)
                }
              >
                <option value="">Qualquer quantidade</option>
                <option value="1">1 ou mais</option>
                <option value="2">2 ou mais</option>
                <option value="3">3 ou mais</option>
                <option value="4">4 ou mais</option>
              </select>
            </label>
            <div className="properties-price-range">
              <label className="properties-filter-field">
                <span>Preço mínimo</span>
                <div className="properties-price-input-wrap">
                  <span aria-hidden="true">R$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatPriceInput(minPrice)}
                    onChange={(event) =>
                      updateSearchParam(
                        "minPrice",
                        parsePriceInput(event.target.value),
                      )
                    }
                    placeholder="Sem mínimo"
                    aria-label="Preço mínimo em reais"
                  />
                </div>
              </label>
              <label className="properties-filter-field">
                <span>Preço máximo</span>
                <div className="properties-price-input-wrap">
                  <span aria-hidden="true">R$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatPriceInput(maxPrice)}
                    onChange={(event) =>
                      updateSearchParam(
                        "maxPrice",
                        parsePriceInput(event.target.value),
                      )
                    }
                    placeholder="Sem máximo"
                    aria-label="Preço máximo em reais"
                  />
                </div>
              </label>
            </div>
          </section>
        )}

        {filteredProperties.length > 0 ? (
          <div
            className={`properties-grid properties-grid--${cardsPerRow}`}
            style={{ "--properties-columns": cardsPerRow }}
          >
            {filteredProperties.map((property) => (
              <PropertyCard
                key={property.code}
                property={property}
                href={`https://www.dalmosouza.com.br/properties.html?search=${property.code}`}
              />
            ))}
          </div>
        ) : (
          <div className="properties-empty" role="status">
            <h2>Nenhum imóvel encontrado</h2>
            <p>Experimente remover alguns filtros para ver mais opções.</p>
            <button
              className="properties-empty-clear"
              type="button"
              onClick={clearFilters}
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>
    </main>
  );
};

export default Properties;
